import React, { useCallback, useEffect, useId, useRef } from 'react';
import Icone from '../primitivos/Icone';

/**
 * Diálogo base do design system (at-58, a pedido de Victor em 09/09/2026).
 *
 * 🔴 **Existe para encerrar os 11 overlays distintos** medidos em 08/09/2026:
 * `.modal-overlay`, `.confirmacao-overlay`, `.filtros-overlay`,
 * `.ti-modal-overlay`, `.importacao-sga-overlay`, `.info-envio-overlay`… — cada
 * modal do app reimplementou o próprio fundo escurecido, com 22 arquivos CSS em
 * `components/modal/`. Cada um resolveu (ou esqueceu) Esc, clique fora e foco por
 * conta própria.
 *
 * ⚠️ O `plan-c` §4.5 adiava este componente para a frente D, com um motivo real:
 * os modais são compartilhados com telas legadas, então mexer neles espalha risco
 * fora do escopo. **Victor pediu explicitamente na at-58** — então a regra passa a
 * ser: migrar **um modal por vez**, conferindo antes quem mais o usa e se o CSS
 * dele carrega global de que outras telas dependem (foi o caso aqui).
 *
 * ## O que ele resolve, e que os modais soltos não resolviam
 *
 *   • **Esc** e **clique fora** fecham — e nunca durante uma ação em andamento;
 *   • **o foco entra** no diálogo ao abrir e **volta** para quem o abriu ao
 *     fechar. Sem isso o teclado continuava no formulário atrás;
 *   • **Tab circula dentro** do diálogo. Declarar `aria-modal="true"` sem prender
 *     o foco é uma promessa falsa ao leitor de tela;
 *   • o título é um `<h2>` de verdade, ligado por `aria-labelledby`.
 *
 * ⚠️ **NÃO trava a rolagem do fundo**, e isso é deliberado: quem rola neste app é
 * o container (`<main>` no shell novo, `.main-containt` no legado), não o `<body>`
 * — que já é `overflow: hidden`. Travar exigiria descobrir qual container é o de
 * cada tela, e o `ModalConfirmacao` (que serve quase o app) nunca travou sem que
 * ninguém reclamasse. Inventar o mecanismo aqui seria arriscar em troca de nada.
 *
 * ⚠️ **Renderiza no lugar, sem portal** — como o `ModalConfirmacao`. O overlay é
 * `position: fixed`, o que basta porque nenhum ancestral do conteúdo tem
 * `transform` (que faria o `fixed` se ancorar nele). Ao usar dentro de container
 * transformado, isto precisa ser revisto.
 */

interface PropsModal {
  aberto: boolean;
  /** Vai no `<h2>` e nomeia o diálogo para o leitor de tela. */
  titulo: string;
  /** Linha de apoio abaixo do título. */
  descricao?: string;
  tamanho?: 'sm' | 'md' | 'lg';
  /**
   * Ação em andamento: trava Esc, clique fora e o botão de fechar.
   *
   * ⚠️ Mesmo papel do `processando` do `ModalConfirmacao` — fechar no meio de uma
   * gravação deixaria o usuário sem saber se ela aconteceu.
   */
  ocupado?: boolean;
  aoFechar: () => void;
  /** Barra de ações no pé. Sem ela, o diálogo não tem rodapé. */
  rodape?: React.ReactNode;
  /**
   * Alinhamento do rodapé no desktop. Padrão `centro`.
   *
   * ⚠️ **`direita` é para PAR DE AÇÕES** (Cancelar/Aplicar), a convenção de
   * botão de diálogo — foi exatamente o caso que este componente já previa em
   * comentário desde a at-58 e que chegou na at-65, com o modal de filtros da
   * carteira. `centro` continua sendo o certo para o conteúdo que hoje ocupa
   * este slot em 9 modais: a **paginação** de uma lista de escolha, que à direita
   * fica deslocada sob uma lista de largura total.
   *
   * ⚠️ No mobile os dois se comportam igual (`flex-col` + stretch): botão de
   * largura total é o alvo de toque certo, e a ordem visual já diz a hierarquia.
   */
  alinharRodape?: 'centro' | 'direita';
  children: React.ReactNode;
}

const POR_TAMANHO: Record<NonNullable<PropsModal['tamanho']>, string> = {
  sm: 'tw-max-w-sm',
  md: 'tw-max-w-lg',
  lg: 'tw-max-w-2xl',
};

/** Elementos que podem receber foco dentro do diálogo. */
const FOCAVEIS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Modal: React.FC<PropsModal> = ({
  aberto,
  titulo,
  descricao,
  tamanho = 'md',
  ocupado = false,
  aoFechar,
  rodape,
  alinharRodape = 'centro',
  children,
}) => {
  const base = useId();
  const idTitulo = `${base}-titulo`;
  const idDescricao = `${base}-descricao`;

  const dialogo = useRef<HTMLDivElement>(null);
  /**
   * O corpo, separado do diálogo inteiro só para o foco inicial.
   *
   * ⚠️ Buscar o primeiro focável no diálogo **todo** cairia no botão "Fechar",
   * que está no `<header>` e portanto vem antes no DOM — abrir um diálogo de
   * escolha com o foco em "Fechar" é o oposto do que se quer. O teste pegou isso.
   */
  const corpo = useRef<HTMLDivElement>(null);
  /** Quem tinha o foco antes de abrir — para devolvê-lo ao fechar. */
  const focoAnterior = useRef<HTMLElement | null>(null);

  const fecharSeLivre = useCallback(() => {
    if (!ocupado) aoFechar();
  }, [ocupado, aoFechar]);

  /*
   * Foco: entra ao abrir, volta ao fechar.
   *
   * Alvo: o **primeiro focável do corpo** quando já existe (num diálogo de
   * escolha isso deixa a primeira opção a um Enter de distância); senão o próprio
   * container, que é `tabIndex={-1}` — o leitor de tela anuncia o título.
   *
   * ⚠️ **Com conteúdo assíncrono, o alvo é o container** — e esse é o caso comum,
   * porque quase todo modal deste app busca a lista ao abrir: quando o efeito
   * roda, o corpo ainda mostra o `Skeleton` e não há o que focar. O Tab a partir
   * dali alcança a lista normalmente. **Não** tente re-focar quando os dados
   * chegarem: isso roubaria o foco de quem já tivesse começado a navegar ou a
   * digitar numa busca.
   */
  useEffect(() => {
    if (!aberto) return undefined;

    focoAnterior.current = document.activeElement as HTMLElement | null;

    const primeiro = corpo.current?.querySelector<HTMLElement>(FOCAVEIS);
    (primeiro ?? dialogo.current)?.focus();

    return () => {
      // `focus` pode não existir (elemento removido do DOM enquanto o modal
      // estava aberto) — devolver foco é conveniência, não pode lançar.
      if (typeof focoAnterior.current?.focus === 'function') focoAnterior.current.focus();
    };
  }, [aberto]);

  /** Esc fecha; Tab circula dentro do diálogo. */
  useEffect(() => {
    if (!aberto) return undefined;

    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        fecharSeLivre();
        return;
      }

      if (evento.key !== 'Tab') return;

      const focaveis = Array.from(dialogo.current?.querySelectorAll<HTMLElement>(FOCAVEIS) ?? []);
      if (focaveis.length === 0) return;

      const primeiro = focaveis[0];
      const ultimo = focaveis[focaveis.length - 1];
      const atual = document.activeElement;

      // Só interceptamos nas pontas: no meio, o Tab nativo já faz o certo.
      if (!evento.shiftKey && atual === ultimo) {
        evento.preventDefault();
        primeiro.focus();
      } else if (evento.shiftKey && (atual === primeiro || atual === dialogo.current)) {
        evento.preventDefault();
        ultimo.focus();
      }
    };

    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [aberto, fecharSeLivre]);

  if (!aberto) return null;

  return (
    <div
      /* `items-end` no mobile: o diálogo sobe do rodapé, onde o dedo já está.
         A partir de `sm` volta a ser centralizado. */
      className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-end tw-justify-center tw-bg-black/50 tw-p-0 sm:tw-items-center sm:tw-p-4"
      onClick={fecharSeLivre}
    >
      <div
        ref={dialogo}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        aria-describedby={descricao ? idDescricao : undefined}
        tabIndex={-1}
        /* O clique no conteúdo não pode borbulhar para o overlay, senão
           escolher um item fecharia por "clique fora". */
        onClick={(evento) => evento.stopPropagation()}
        className={[
          'tw-flex tw-max-h-[85vh] tw-w-full tw-flex-col tw-overflow-hidden tw-bg-white',
          'tw-rounded-t-2xl tw-shadow-[0_8px_32px_rgba(0,0,0,0.24)] sm:tw-rounded-2xl',
          'focus-visible:tw-outline-none',
          POR_TAMANHO[tamanho],
        ].join(' ')}
      >
        <header className="tw-flex tw-shrink-0 tw-items-start tw-gap-3 tw-border-b tw-border-superficie-borda tw-px-5 tw-py-4">
          <div className="tw-min-w-0 tw-flex-1">
            <h2
              id={idTitulo}
              className="tw-m-0 tw-font-marca tw-text-lg tw-font-bold tw-text-texto-titulo"
            >
              {titulo}
            </h2>
            {descricao && (
              <p id={idDescricao} className="tw-mt-1 tw-mb-0 tw-text-xs tw-text-texto-suave">
                {descricao}
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={fecharSeLivre}
            disabled={ocupado}
            aria-label="Fechar"
            className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-texto-suave tw-cursor-pointer hover:tw-bg-superficie hover:tw-text-texto-titulo disabled:tw-cursor-not-allowed disabled:tw-opacity-50 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-brand/40"
          >
            <Icone nome="fechar" tamanho={18} />
          </button>
        </header>

        {/* O corpo é quem rola: o cabeçalho e o rodapé ficam sempre visíveis,
            mesmo numa lista longa (as cotas passam de 16 itens). */}
        <div ref={corpo} className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-5 tw-py-4">
          {children}
        </div>

        {rodape && (
          /*
           * ⚠️ **Centralizado por padrão** (pedido de Victor, 09/09/2026). Em 9 dos
           * 10 modais o `rodape` é a paginação de uma lista de escolha, e ali o canto
           * direito deixa o card deslocado sob uma lista que ocupa a largura toda. No
           * mobile o `flex-col` + stretch faz o card ocupar a linha inteira, que é o
           * certo para o alvo de toque.
           *
           * ✅ **O par de ações previsto aqui chegou na at-65** — o modal de filtros da
           * carteira (Limpar · Cancelar · Aplicar) —, e com ele a prop `alinharRodape`,
           * como este comentário antecipava. O default não mudou.
           *
           * ⚠️ Comentário SEM chaves: aqui é o interior de `{rodape && ( … )}`, que é
           * contexto de expressão JS — um `{​/* … *​/}` seria lido como objeto literal
           * e quebra o build (mesmo caso do `Field.tsx`).
           */
          <footer
            className={[
              'tw-flex tw-shrink-0 tw-flex-col tw-gap-2 tw-border-t tw-border-superficie-borda tw-px-5 tw-py-3 sm:tw-flex-row',
              alinharRodape === 'direita' ? 'sm:tw-justify-end' : 'sm:tw-justify-center',
            ].join(' ')}
          >
            {rodape}
          </footer>
        )}
      </div>
    </div>
  );
};

export default Modal;
